import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';

import { addNegativeToast } from 'modules/toastContainer';
import { NoOp } from 'utils/noop';

import { useTranslations } from '../hooks/useTranslations';
import { TakeoverContext, TakeoverContextState } from './Takeover';
import { TakeoverIntent, UploadCompleteConfig } from '../types';

export const TakeoverContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { t } = useTranslations();
  const dispatchHook = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isWarningOverlayOpen, setIsWarningOverlayOpen] = useState(false);
  const [currentIntent, setCurrentIntent] = useState(TakeoverIntent.UPLOAD);
  const [initialIntent, setInitialIntent] = useState(TakeoverIntent.UPLOAD);

  const handleNewFiles = () => {
    setCurrentIntent(TakeoverIntent.UPLOAD);
    // Only set initialIntent when opening the takeover (not during internal transitions)
    if (!isOpen) {
      setInitialIntent(TakeoverIntent.UPLOAD);
    }
    setIsOpen(true);
  };

  const handleEditFiles = () => {
    setInitialIntent(TakeoverIntent.EDIT);
    setCurrentIntent(TakeoverIntent.EDIT);
    setIsOpen(true);
  };

  const handleImportFromUrl = () => {
    setInitialIntent(TakeoverIntent.IMPORT_FROM_URL);
    setCurrentIntent(TakeoverIntent.IMPORT_FROM_URL);
    setIsOpen(true);
  };

  const handleClose = (warnBeforeClose: boolean, onConfirmClose: NoOp) => {
    if (!warnBeforeClose) {
      onConfirmClose();
      setIsOpen(false);
      setIsWarningOverlayOpen(false);
    } else {
      setIsWarningOverlayOpen(true);
    }
  };

  const handleUploadComplete = ({
    filesToDismiss,
    newFiles,
    setPendingImages,
    uploadFailures,
    uploadSuccesses,
  }: UploadCompleteConfig) => {
    if (uploadSuccesses.length) {
      // update the list of pending assets so that they can be clicked for details
      setPendingImages((pendingImgs) => {
        const newPendingImgs = [...pendingImgs];
        uploadSuccesses.forEach((upld) => {
          const idx = newPendingImgs.findIndex((pImg) => pImg.id === upld.id);
          if (idx > -1) {
            newPendingImgs[idx].asset = upld.asset;
            newPendingImgs[idx].isSelected = false;
          }
        });
        return newPendingImgs;
      });
    }
    // if the user closed the Takeover during upload, show toasts for any errors
    if (!isOpen) {
      // error toasts for files that failed processing (processImage) from STEP 2
      filesToDismiss.forEach((file) => {
        dispatchHook(
          addNegativeToast(
            `${t('errors.canNotAdd', { fileName: file.file.name })} ${
              file.validationError || t(`errors.processImage.${file.processError}`)
            }`,
          ),
        );
      });
      // error toasts for files that failed uploading
      uploadFailures.forEach(({ id }) => {
        // NOTE: the uploadError will not exist on this version of the failedFile, so show generic 'failed'
        const failedFile = newFiles.find((file) => file.id === id);
        if (failedFile) {
          dispatchHook(
            addNegativeToast(
              `${t('errors.canNotAdd', { fileName: failedFile.file.name })} ${t(
                `errors.uploadAsset.failed`,
              )}`,
            ),
          );
        }
      });
    }
  };

  const context: TakeoverContextState = {
    currentIntent,
    setCurrentIntent,
    initialIntent,
    isOpen,
    isWarningOverlayOpen,
    onClose: handleClose,
    onCloseWarningOverlay: () => setIsWarningOverlayOpen(false),
    onEditFiles: handleEditFiles,
    onImportFromUrl: handleImportFromUrl,
    onNewFiles: handleNewFiles,
    onUploadComplete: handleUploadComplete,
  };

  return <TakeoverContext.Provider value={context}>{children}</TakeoverContext.Provider>;
};
